import { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { useCart } from "@/contexts/CartContext";
import { api, formatApiErrorDetail, money } from "@/lib/api";
import ComplianceBanner from "@/components/ComplianceBanner";
import { toast } from "sonner";
import { CreditCard, CurrencyDollar, CurrencyEth, CheckCircle } from "@phosphor-icons/react";

// Merchant payment handles — update these to reflect your real accounts.
const PAY_CFG = {
  cashapp_tag:   "$PRMUpeptides",
  merchant_email: "orders@prmushop.com",
  support_phone: "(214) 402-3432",
};

const PAYMENT_METHODS = [
  {
    id: "invoice",
    label: "Credit/Debit Card Via Secure Invoice",
    icon: <CreditCard size={18} weight="duotone" />,
    body: (_ctx) => (
      <p className="text-sm text-zinc-600">
        After personally reviewing your order, we'll send a secure payment invoice to your email for payment.
      </p>
    ),
  },
  {
    id: "alt",
    label: "Crypto, Zelle, Venmo or Cash App",
    icon: <CurrencyEth size={18} weight="duotone" />,
    body: (_ctx) => (
      <p className="text-sm text-zinc-600">
        After personally reviewing your order, we'll send a secure payment invoice to your email for payment.
      </p>
    ),
  },
];

export default function Checkout() {
  const { user } = useAuth();
  const { items, subtotal_cents, clear } = useCart();
  const [addr, setAddr] = useState({
    full_name: "", line1: "", line2: "", city: "", state: "", postal_code: "", country: "US", phone: "",
  });
  const [code, setCode] = useState("");
  const [quote, setQuote] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [method, setMethod] = useState("invoice");
  const [confirmResearch, setConfirmResearch] = useState(false);
  const [marketing, setMarketing] = useState(false);
  const nav = useNavigate();

  useEffect(() => {
    if (user === null) return;
    if (!user) nav("/login?next=/checkout");
  }, [user, nav]);

  useEffect(() => {
    if (!items.length) return;
    api.post("/quote", {
      items: items.map((i) => ({ product_id: i.product_id, variant_sku: i.variant_sku, quantity: i.quantity })),
      shipping_address: { full_name: "temp", line1: "temp", city: "temp", postal_code: "0", country: "US" },
      discount_code: code || undefined,
    }).then((r) => setQuote(r.data)).catch(() => {});
  }, [items, code]);

  const total_cents = quote?.total_cents ?? subtotal_cents;

  const submit = async (e) => {
    e.preventDefault();
    if (!items.length) return;
    if (!confirmResearch) {
      toast.error("Please confirm the research-use disclaimer to place your order.");
      return;
    }
    setSubmitting(true);
    try {
      const { data } = await api.post("/checkout", {
        items: items.map((i) => ({ product_id: i.product_id, variant_sku: i.variant_sku, quantity: i.quantity })),
        shipping_address: addr,
        billing_address: addr,
        discount_code: code || undefined,
        payment_method: method,
        marketing_opt_in: marketing,
      });
      clear();
      toast.success("Order placed — payment instructions coming to your email");
      nav(`/account/orders/${data.id}`);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSubmitting(false);
    }
  };

  if (!user || user === null) return null;

  return (
    <div className="page-enter">
      <ComplianceBanner />
      <div className="mx-auto max-w-[1400px] px-6 lg:px-10 py-16 grid lg:grid-cols-12 gap-10">
        <form onSubmit={submit} className="lg:col-span-8 space-y-10" data-testid="checkout-form">
          <div>
            <div className="overline mb-3">Checkout</div>
            <h1 className="font-display text-4xl md:text-5xl font-bold tracking-tight">Shipping details</h1>
          </div>
          <div className="grid md:grid-cols-2 gap-4">
            <F label="Full name" v={addr.full_name} on={(v) => setAddr({ ...addr, full_name: v })} tid="co-name" required />
            <F label="Phone" v={addr.phone} on={(v) => setAddr({ ...addr, phone: v })} tid="co-phone" />
            <F label="Address line 1" v={addr.line1} on={(v) => setAddr({ ...addr, line1: v })} tid="co-line1" required full />
            <F label="Address line 2" v={addr.line2} on={(v) => setAddr({ ...addr, line2: v })} tid="co-line2" full />
            <F label="City" v={addr.city} on={(v) => setAddr({ ...addr, city: v })} tid="co-city" required />
            <F label="State / region" v={addr.state} on={(v) => setAddr({ ...addr, state: v })} tid="co-state" />
            <F label="Postal code" v={addr.postal_code} on={(v) => setAddr({ ...addr, postal_code: v })} tid="co-postal" required />
            <F label="Country" v={addr.country} on={(v) => setAddr({ ...addr, country: v })} tid="co-country" required />
          </div>

          {/* Payment Information */}
          <div>
            <h2 className="font-display text-2xl font-bold mb-4">Payment Information</h2>
            <div className="space-y-3">
              {PAYMENT_METHODS.map((m) => {
                const active = method === m.id;
                return (
                  <div key={m.id} className="rounded-xl overflow-hidden border border-black/10" data-testid={`pm-${m.id}`}>
                    <label className="flex items-center gap-3 px-5 py-4 cursor-pointer bg-white">
                      <input
                        type="radio"
                        name="pm"
                        value={m.id}
                        checked={active}
                        onChange={() => setMethod(m.id)}
                        className="h-4 w-4 accent-blue-600"
                        data-testid={`pm-radio-${m.id}`}
                      />
                      <span className="flex items-center gap-2 font-semibold text-black">{m.label} {m.icon}</span>
                    </label>
                    {active && (
                      <div className="px-5 pb-5 pt-1 bg-zinc-50 border-t border-black/5" data-testid={`pm-body-${m.id}`}>
                        {m.body({ total_cents })}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>

          {/* Privacy blurb */}
          <p className="text-sm text-zinc-600 max-w-2xl">
            Your personal data will be used to process your order, support your experience throughout this website, and for other purposes described in our{" "}
            <Link to="/privacy" className="text-blue-600 underline"><b>privacy policy</b></Link>.
          </p>

          {/* Research + age confirmation */}
          <label className="flex items-start gap-3 border border-black/10 rounded-xl p-4 bg-zinc-50 cursor-pointer" data-testid="co-confirm-label">
            <input
              type="checkbox"
              checked={confirmResearch}
              onChange={(e) => setConfirmResearch(e.target.checked)}
              className="mt-0.5 h-5 w-5 accent-blue-600"
              data-testid="co-confirm-research"
              required
            />
            <span className="text-sm text-zinc-700">
              I confirm these products are for laboratory and research use only. I am 21 years of age or older and will not consume or administer these products. <span className="text-red-500">*</span>
            </span>
          </label>

          <label className="flex items-center gap-3 cursor-pointer">
            <input
              type="checkbox"
              checked={marketing}
              onChange={(e) => setMarketing(e.target.checked)}
              className="h-4 w-4 accent-blue-600"
              data-testid="co-marketing"
            />
            <span className="text-sm text-zinc-700">I would like to receive marketing emails and updates.</span>
          </label>

          <div>
            <div className="overline mb-2">Discount / partner code</div>
            <div className="flex gap-2 max-w-md">
              <input
                value={code}
                onChange={(e) => setCode(e.target.value.toUpperCase())}
                placeholder="DEMO10"
                className="flex-1 border border-black/20 px-3 py-2 outline-none focus:border-black bg-white text-sm font-mono"
                data-testid="co-code"
              />
            </div>
          </div>

          <button
            className="btn-prm w-full md:w-auto justify-center"
            disabled={submitting || !items.length || !confirmResearch}
            data-testid="co-submit"
          >
            {submitting ? "Placing order…" : <>Place Order <CheckCircle size={16} weight="bold" /></>}
          </button>
        </form>

        <aside className="lg:col-span-4">
          <div className="border border-black/10 p-6 sticky top-24 rounded-xl">
            <div className="overline mb-3">Order</div>
            <ul className="space-y-2 text-sm">
              {items.map((i) => (
                <li key={i.key} className="flex justify-between font-mono text-xs">
                  <span>{i.quantity}× {i.product_name}{i.variant_label ? ` (${i.variant_label})` : ""}</span>
                  <span>{money(i.unit_price_cents * i.quantity)}</span>
                </li>
              ))}
            </ul>
            <dl className="mt-5">
              <div className="data-row"><dt>Subtotal</dt><dd>{money(quote?.subtotal_cents ?? subtotal_cents)}</dd></div>
              {quote?.discount_cents > 0 && (
                <div className="data-row"><dt>Discount ({quote.discount_code})</dt><dd>-{money(quote.discount_cents)}</dd></div>
              )}
              <div className="data-row"><dt>Shipping</dt><dd>{quote?.shipping_cents === 0 ? "Free" : money(quote?.shipping_cents ?? 0)}</dd></div>
              <div className="data-row"><dt><b>Total</b></dt><dd><b>{money(total_cents)}</b></dd></div>
            </dl>
            <Link to="/cart" className="mt-4 inline-block text-sm link-underline">Edit cart</Link>
          </div>
        </aside>
      </div>
    </div>
  );
}

function F({ label, v, on, tid, required, full }) {
  return (
    <label className={`block ${full ? "md:col-span-2" : ""}`}>
      <div className="overline mb-1">{label}</div>
      <input
        value={v}
        required={required}
        onChange={(e) => on(e.target.value)}
        className="w-full border border-black/20 px-3 py-2 outline-none focus:border-black bg-white text-sm"
        data-testid={tid}
      />
    </label>
  );
}
